import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  CheckCircle,
  XCircle,
  Clock,
  User,
  Stethoscope,
  Pill,
  Calendar,
  Shield,
  QrCode,
  Download,
  Printer
} from 'lucide-react';

interface PrescriptionData {
  cuir: string;
  issuedAt: string;
  expiresAt: string;
  professional: {
    name: string;
    license: string;
    specialty: string;
  };
  patient: {
    name: string;
    dni: string;
    sex?: string;
    birthDate?: string;
    healthInsurance?: string;
    insuranceNumber?: string;
  };
  diagnosis: string;
  items: Array<{
    medication: string;
    commercialName?: string;
    pharmaceuticalForm?: string;
    presentation: string;
    quantity: number;
    dosage: string;
    duration: string;
    notes?: string;
  }>;
}

interface ValidationResult {
  valid: boolean;
  status: string;
  message: string;
  prescription: PrescriptionData;
}

const PublicPrescriptionValidator: React.FC = () => {
  const { cuir } = useParams<{ cuir: string }>();
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [validation, setValidation] = useState<ValidationResult | null>(null);
  const [manualCuir, setManualCuir] = useState('');

  useEffect(() => {
    if (cuir) {
      validatePrescription(cuir);
    } else {
      setLoading(false);
    }
  }, [cuir]);

  const validatePrescription = async (code: string) => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/prescriptions/validate/${code}`);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Error validando receta');
      }

      setValidation(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  const handleManualValidation = (e: React.FormEvent) => {
    e.preventDefault();
    if (manualCuir.trim()) {
      window.location.href = `/receta/${manualCuir.trim()}`;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-AR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const getStatusInfo = (status: string) => {
    switch (status) {
      case 'valid':
        return { icon: CheckCircle, color: 'text-green-500', bg: 'bg-green-50', border: 'border-green-200', title: 'Receta Válida', desc: 'Esta receta es auténtica y puede ser dispensada' };
      case 'dispensed':
        return { icon: CheckCircle, color: 'text-blue-500', bg: 'bg-blue-50', border: 'border-blue-200', title: 'Receta Dispensada', desc: 'Esta receta ya fue dispensada anteriormente' };
      case 'expired':
        return { icon: Clock, color: 'text-yellow-500', bg: 'bg-yellow-50', border: 'border-yellow-200', title: 'Receta Expirada', desc: 'Esta receta ha superado su fecha de validez' };
      case 'cancelled':
        return { icon: XCircle, color: 'text-red-500', bg: 'bg-red-50', border: 'border-red-200', title: 'Receta Cancelada', desc: 'Esta receta fue cancelada por el profesional' };
      default:
        return { icon: CheckCircle, color: 'text-green-500', bg: 'bg-green-50', border: 'border-green-200', title: 'Receta Válida', desc: 'Esta receta es auténtica' };
    }
  };

  // Loading
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Validando receta...</p>
        </div>
      </div>
    );
  }

  // Search form (no CUIR)
  if (!cuir && !validation) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 py-12 px-4">
        <div className="max-w-lg mx-auto">
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-green-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
              <Shield className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900">ConsultAR</h1>
            <p className="text-gray-600 mt-1">Validador de Recetas Electrónicas</p>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Validar Receta</h2>
            <p className="text-gray-600 mb-6">Ingrese el código CUIR de la receta para verificar su autenticidad</p>

            <form onSubmit={handleManualValidation} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Código CUIR</label>
                <input
                  type="text"
                  value={manualCuir}
                  onChange={(e) => setManualCuir(e.target.value.toUpperCase())}
                  placeholder="CAR-20260128-MP12345-000001-ED"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-center"
                />
              </div>

              <button
                type="submit"
                disabled={!manualCuir.trim()}
                className="w-full py-3 bg-gradient-to-r from-blue-500 to-green-500 text-white rounded-xl font-semibold hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Validar Receta
              </button>
            </form>

            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="flex items-center gap-3 text-sm text-gray-500">
                <QrCode className="w-5 h-5" />
                <span>También puede escanear el código QR de la receta</span>
              </div>
            </div>
          </div>

          <div className="mt-8 text-center text-sm text-gray-500">
            <p>Sistema de Recetas Electrónicas</p>
            <p>Registrado en ReNaPDiS - Ministerio de Salud</p>
          </div>
        </div>
      </div>
    );
  }

  // Error
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 py-12 px-4">
        <div className="max-w-lg mx-auto">
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-10 h-10 text-red-500" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Receta No Encontrada</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <a href="/receta" className="inline-block px-6 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors">
              Intentar con otro código
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Validation result
  if (validation) {
    const statusInfo = getStatusInfo(validation.status);
    const StatusIcon = statusInfo.icon;
    const rx = validation.prescription;

    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 py-8 px-4">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-green-500 rounded-xl flex items-center justify-center mx-auto mb-2 shadow-lg">
              <Shield className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">ConsultAR</h1>
            <p className="text-sm text-gray-500">Validador de Recetas Electrónicas</p>
          </div>

          <div className={`${statusInfo.bg} ${statusInfo.border} border-2 rounded-2xl p-6 mb-6`}>
            <div className="flex items-center gap-4">
              <div className={`w-16 h-16 rounded-full ${statusInfo.bg} flex items-center justify-center`}>
                <StatusIcon className={`w-10 h-10 ${statusInfo.color}`} />
              </div>
              <div>
                <h2 className={`text-2xl font-bold ${statusInfo.color}`}>{statusInfo.title}</h2>
                <p className="text-gray-600">{statusInfo.desc}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
            <div className="text-center">
              <p className="text-sm text-gray-500 mb-1">Código CUIR</p>
              <p className="text-xl font-mono font-bold text-purple-600">{rx.cuir}</p>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg overflow-hidden mb-6">
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Stethoscope className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Profesional</p>
                  <p className="font-semibold text-gray-900">{rx.professional.name}</p>
                  <p className="text-sm text-gray-600">{rx.professional.specialty} • Mat. {rx.professional.license}</p>
                </div>
              </div>
            </div>

            <div className="p-6 border-b border-gray-100">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <User className="w-6 h-6 text-green-600" />
                </div>
                <div className="space-y-0.5">
                  <p className="text-sm text-gray-500">Paciente</p>
                  <p className="font-semibold text-gray-900">{rx.patient.name}</p>
                  {rx.patient.dni && (
                    <p className="text-sm text-gray-600">DNI: {rx.patient.dni}</p>
                  )}
                  {rx.patient.sex && (
                    <p className="text-sm text-gray-600">
                      Sexo: {{ F: 'Femenino', M: 'Masculino', X: 'No binario' }[rx.patient.sex] ?? rx.patient.sex}
                    </p>
                  )}
                  {rx.patient.birthDate && (
                    <p className="text-sm text-gray-600">
                      Fecha Nac.: {new Date(rx.patient.birthDate).toLocaleDateString('es-AR')}
                    </p>
                  )}
                  <p className="text-sm text-gray-600">
                    OOSS/Plan Médico:{' '}
                    {rx.patient.healthInsurance
                      ? rx.patient.insuranceNumber
                        ? `${rx.patient.healthInsurance} / ${rx.patient.insuranceNumber}`
                        : rx.patient.healthInsurance
                      : 'Particular'}
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6 border-b border-gray-100">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Calendar className="w-6 h-6 text-purple-600" />
                </div>
                <div className="flex-1">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Emitida</p>
                      <p className="font-medium text-gray-900">{formatDate(rx.issuedAt)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Vence</p>
                      <p className="font-medium text-gray-900">{formatDate(rx.expiresAt)}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {rx.diagnosis && (
              <div className="p-6 border-b border-gray-100">
                <p className="text-sm text-gray-500 mb-1">Diagnóstico</p>
                <p className="text-gray-900">{rx.diagnosis}</p>
              </div>
            )}

            <div className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <Pill className="w-5 h-5 text-orange-500" />
                <p className="font-semibold text-gray-900">Medicamentos</p>
              </div>
              <div className="space-y-3">
                {rx.items.map((item, index) => (
                  <div key={index} className="bg-gray-50 rounded-xl p-4">
                    <p className="font-medium text-gray-900">{item.medication}</p>
                    <div className="mt-1 text-sm text-gray-600 space-y-1">
                      {item.presentation && <p>Presentación: {item.presentation}</p>}
                      <p>Cantidad: {item.quantity} unidad(es)</p>
                      {item.dosage && <p>Posología: {item.dosage}</p>}
                      {item.duration && <p>Duración: {item.duration}</p>}
                      {item.notes && <p className="italic">Nota: {item.notes}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {validation.status === 'valid' && (
            <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
              <p className="text-sm text-gray-500 mb-4 text-center">Para dispensar esta receta, registre la dispensación en su sistema</p>
              <div className="flex gap-3">
                <button onClick={() => window.print()} className="flex-1 flex items-center justify-center gap-2 py-3 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors">
                  <Printer className="w-5 h-5" />
                  Imprimir
                </button>
                <button onClick={() => window.open(`${API_URL}/api/prescriptions/${rx.cuir}/pdf`, '_blank')} className="flex-1 flex items-center justify-center gap-2 py-3 bg-gradient-to-r from-blue-500 to-green-500 text-white rounded-xl hover:shadow-lg transition-all">
                  <Download className="w-5 h-5" />
                  Descargar PDF
                </button>
              </div>
            </div>
          )}

          {/* Leyendas obligatorias Decreto 63/2024 */}
          <div className="bg-gray-50 border border-gray-200 rounded-2xl p-4 text-xs text-gray-500 space-y-2">
            <p>
              <span className="font-medium text-gray-700">Firma: </span>
              Este documento ha sido firmado -electrónica o digitalmente según corresponda-
              por {rx.professional.name}.
            </p>
            <p>
              <span className="font-medium text-gray-700">Registro: </span>
              Esta receta fue creada por un emisor inscripto y validado en el Registro de
              Recetarios Electrónicos del Ministerio de Salud de la Nación
              -{process.env.REACT_APP_PRESCRIPTION_RL_LEGAJO || 'RL PENDIENTE DE ASIGNACIÓN'}.
            </p>
          </div>

          <div className="text-center text-sm text-gray-500">
            <p>Sistema de Recetas Electrónicas - ConsultAR</p>
            <p>Registrado en el Ministerio de Salud de la Nación</p>
            <p className="mt-2">
              <a href="/receta" className="text-blue-500 hover:underline">Validar otra receta</a>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default PublicPrescriptionValidator;